import { cn } from "@/shared/lib/utils";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useClientId } from "@/shared/hooks/useClientId";
import { supabase } from "@/shared/integrations/supabase/client";
import { usePropertyOverviewData } from "@/features/properties/hooks/usePropertyOverviewData";
import { usePropertyAttributes } from "@/features/properties/hooks/usePropertyAttributes";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { TooltipProvider } from "@/shared/ui/tooltip";

import { AssetDetailPanelLoader } from "@/features/assets/pages/clientAssets/AssetDetailPanelLoader";
import { DeficienciesCard } from "./propertyOverview/DeficienciesCard";
import { RecommendedStepsCard } from "./propertyOverview/RecommendedStepsCard";
import { DataCompletenessCard } from "./propertyOverview/DataCompletenessCard";
import {
  exportDeficienciesCSV,
  exportDeficienciesXLSX,
  exportStepsCSV,
  exportStepsXLSX,
  type RecommendedStepSection,
} from "./propertyOverview/csvExports";
import {
  getGroupLabel,
  getDeficiencyCategory,
  getDeficiencyTitle,
  normalizeDeficiencySeverity,
  severityToPriorityFromDeficiency,
  type DeficiencySeverity,
} from "./propertyOverview/deficiencyHelpers";
import { useAuditDeficiencies } from "@/features/audit/useAuditDeficiencies";
import {
  VISIBLE_ASSET_COMPLETENESS_CATEGORIES,
  getSurveyCompletenessRows,
  type AssetCompletenessRow,
  type PropertyAssetCompletenessSource,
} from "./propertyOverview/completenessHelpers";

interface PropertyOverviewTabProps {
  propertyId: string;
}

export function PropertyOverviewTab({ propertyId }: PropertyOverviewTabProps) {
  const { clientId } = useClientId();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePropertyOverviewData(propertyId, clientId ?? undefined);
  const { data: surveyAttributes = [], isLoading: surveyAttributesLoading } = usePropertyAttributes(propertyId);
  const [completenessView, setCompletenessView] = useState<"assets" | "survey">("survey");
  const [drawerAssetId, setDrawerAssetId] = useState<string | null>(null);

  useEffect(() => {
    setCompletenessView("survey");
  }, [propertyId]);

  const {
    deficiencies: auditDeficiencies,
    recommendations: auditRecommendations,
    score: auditScore,
    recommendationTotal: auditRecommendationTotal,
  } = useAuditDeficiencies(propertyId);
  const deficiencyData = { deficiencies: auditDeficiencies, recommendations: auditRecommendations };

  const { data: assetCompletenessRows = [], isLoading: assetCompletenessLoading } = useQuery<AssetCompletenessRow[]>({
    queryKey: ["property-asset-completeness-direct", propertyId],
    queryFn: async () => {
      const { data: rows, error: assetErr } = await supabase
        .from("property_assets")
        .select("id, name, category_id, manufacturer, model_number, nameplate_photo_url, deleted_at, status, asset_categories(name)")
        .eq("property_id", propertyId);

      if (assetErr) throw assetErr;

      const hasValue = (value: string | null | undefined) => Boolean(value && value.trim() !== "");
      const getCategoryName = (row: PropertyAssetCompletenessSource) => {
        const relation = row.asset_categories;
        if (Array.isArray(relation)) return relation[0]?.name || "Uncategorized";
        return relation?.name || "Uncategorized";
      };

      const grouped = new Map<
        string,
        {
          categoryId: string;
          name: string;
          totalAssets: number;
          dataComplete: number;
          photosComplete: number;
          flaggedAssets: AssetCompletenessRow["flaggedAssets"];
        }
      >();

      for (const row of (rows ?? []) as unknown as PropertyAssetCompletenessSource[]) {
        if (row.deleted_at) continue;
        if (row.status?.toLowerCase() === "deleted") continue;

        const categoryName = getCategoryName(row);
        if (!VISIBLE_ASSET_COMPLETENESS_CATEGORIES.has(categoryName)) continue;

        const categoryId = row.category_id ?? "uncategorized";
        const current = grouped.get(categoryId) ?? {
          categoryId,
          name: categoryName,
          totalAssets: 0,
          dataComplete: 0,
          photosComplete: 0,
          flaggedAssets: [] as AssetCompletenessRow["flaggedAssets"],
        };

        current.totalAssets += 1;
        const missingData = !(hasValue(row.manufacturer) && hasValue(row.model_number));
        const missingPhoto = !hasValue(row.nameplate_photo_url);
        if (!missingData) current.dataComplete += 1;
        if (!missingPhoto) current.photosComplete += 1;
        if (missingData || missingPhoto) {
          current.flaggedAssets.push({
            id: row.id,
            name: row.name?.trim() || "Unnamed asset",
            missingData,
            missingPhoto,
          });
        }
        grouped.set(categoryId, current);
      }

      return Array.from(grouped.values())
        .map((group) => ({
          categoryId: group.categoryId,
          name: group.name,
          totalAssets: group.totalAssets,
          dataScore: group.totalAssets > 0 ? Math.round((group.dataComplete / group.totalAssets) * 100) : 0,
          photosScore: group.totalAssets > 0 ? Math.round((group.photosComplete / group.totalAssets) * 100) : 0,
          flaggedAssets: group.flaggedAssets.sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!propertyId,
    staleTime: 60_000,
  });

  const surveyCompletenessRows = getSurveyCompletenessRows(surveyAttributes);
  const visibleAssetCompletenessRows = assetCompletenessRows.filter((row) =>
    VISIBLE_ASSET_COMPLETENESS_CATEGORIES.has(row.name)
  );
  const hasCompletenessData = surveyCompletenessRows.length > 0 || visibleAssetCompletenessRows.length > 0;

  // ── Derived data ─────────────────────────────────────────────────────────
  const completionDetails = hasCompletenessData ? null : data?.completionDetails;
  const scoredCompletenessValues = [
    ...visibleAssetCompletenessRows.flatMap((row) => [row.dataScore, row.photosScore]),
    ...surveyCompletenessRows
      .map((row) => row.score)
      .filter((score): score is number => score != null),
  ];
  const overallCompletion = scoredCompletenessValues.length > 0
    ? Math.round(scoredCompletenessValues.reduce((sum, score) => sum + score, 0) / scoredCompletenessValues.length)
    : (data?.completionScore ?? null);
  const assetCategoriesBelowTarget = visibleAssetCompletenessRows.filter((row) =>
    row.dataScore < 90 || row.photosScore < 90
  ).length;
  const surveySectionsBelowTarget = surveyCompletenessRows.filter((row) => row.score < 90).length;
  const activeBelowTargetCount = completenessView === "assets"
    ? assetCategoriesBelowTarget
    : surveySectionsBelowTarget;
  const activeBelowTargetLabel = completenessView === "assets" ? "category" : "section";

  // ── Loading / error states ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-card/30 border border-dashed border-border rounded-xl p-12 text-center">
        <p className="text-sm text-destructive">Failed to load overview data. Please try again later.</p>
      </div>
    );
  }

  // ── Deficiencies by severity/category then recency ────────────────────────
  const defList = deficiencyData.deficiencies;
  const recommendationList = deficiencyData.recommendations;
  const pendingDefs = defList;
  const severityOrder: Record<DeficiencySeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const topDeficiencies = [...pendingDefs]
    .sort((a, b) => {
      const sa = severityOrder[normalizeDeficiencySeverity(a.severity)];
      const sb = severityOrder[normalizeDeficiencySeverity(b.severity)];
      if (sa !== sb) return sa - sb;
      return getDeficiencyTitle(a).localeCompare(getDeficiencyTitle(b));
    })
    .slice(0, 3);
  const deficiencySeverityCounts = defList.reduce(
    (counts, deficiency) => {
      counts[normalizeDeficiencySeverity(deficiency.severity)] += 1;
      return counts;
    },
    { critical: 0, high: 0, medium: 0, low: 0 } satisfies Record<DeficiencySeverity, number>
  );
  const deficiencyCategoryCounts = pendingDefs.reduce((counts, deficiency) => {
    const category = getDeficiencyCategory(deficiency);
    counts.set(category, (counts.get(category) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());

  // ── Recommended steps from the recommendations array in the same JSON ─────
  const deficienciesById = new Map(defList.flatMap((deficiency) => (deficiency.id ? [[deficiency.id, deficiency]] : [])));
  const recommendedStepSections: RecommendedStepSection[] = Array.from(
    recommendationList
      .filter((recommendation) => Boolean(recommendation.line_item?.trim()))
      .reduce(
        (groups, recommendation) => {
          const linkedDeficiency = recommendation.deficiency_id
            ? deficienciesById.get(recommendation.deficiency_id)
            : undefined;
          const category = recommendation.section
            ? getGroupLabel(recommendation.section)
            : linkedDeficiency
              ? getDeficiencyCategory(linkedDeficiency)
              : "Other";
          const current = groups.get(category) ?? {
            category,
            priority: linkedDeficiency ? severityToPriorityFromDeficiency(linkedDeficiency) : normalizeDeficiencySeverity(recommendation.priority),
            totalCost: 0,
            lineItems: [] as { description: string; cost: number | null }[],
          };
          const cost = recommendation.cost_estimate == null || recommendation.cost_estimate === ""
            ? null
            : (() => {
                const raw = recommendation.cost_estimate;
                const num = typeof raw === "number" ? raw : Number(String(raw).replace(/[^0-9.-]/g, ""));
                return Number.isFinite(num) ? num : null;
              })();
          current.totalCost += cost ?? 0;
          current.lineItems.push({
            description: recommendation.line_item || "Recommended step",
            cost,
          });
          const nextPriority = linkedDeficiency
            ? severityToPriorityFromDeficiency(linkedDeficiency)
            : normalizeDeficiencySeverity(recommendation.priority);
          if (severityOrder[nextPriority] < severityOrder[normalizeDeficiencySeverity(current.priority)]) {
            current.priority = nextPriority;
          }
          groups.set(category, current);
          return groups;
        },
        new Map<string, RecommendedStepSection>()
      )
      .values()
  ).sort((a, b) => b.totalCost - a.totalCost || a.category.localeCompare(b.category));
  const totalReplacementCost = recommendedStepSections.reduce((sum, section) => sum + section.totalCost, 0);

  // ── Action button style (reusable) ───────────────────────────────────────
  const actionBtnCls = cn(
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
    "text-xs font-medium",
    "bg-muted/40 text-muted-foreground border border-border/40",
    "hover:bg-muted/70 transition-colors duration-150",
  );

  const openDeficienciesTab = (deficiencyId?: string | null) => {
    if (!clientId) return;
    const params = new URLSearchParams({ tab: "deficiencies" });
    if (deficiencyId) {
      params.set("selectedDeficiencyId", deficiencyId);
    }
    navigate(`/property/${propertyId}?${params.toString()}`);
  };

  const openAssetDetail = (assetId: string) => {
    setDrawerAssetId(assetId);
  };

  const openSurveyQuestion = (fieldId: string) => {
    if (!clientId) return;
    const params = new URLSearchParams({ tab: "site-attributes", focusFieldId: fieldId });
    navigate(`/property/${propertyId}?${params.toString()}`);
  };

  return (
    <TooltipProvider>
      <div className="space-y-5 animate-fade-in">

        <DeficienciesCard
          defList={defList}
          pendingDefs={pendingDefs}
          topDeficiencies={topDeficiencies}
          deficiencySeverityCounts={deficiencySeverityCounts}
          deficiencyCategoryCounts={deficiencyCategoryCounts}
          recommendedStepSectionCount={recommendedStepSections.length}
          actionBtnCls={actionBtnCls}
          onExportCSV={() => exportDeficienciesCSV(defList)}
          onExportXLSX={() => exportDeficienciesXLSX(defList)}
          onOpenDeficiency={openDeficienciesTab}
          score={auditScore}
          recommendationTotal={auditRecommendationTotal ?? totalReplacementCost}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
          <RecommendedStepsCard
            sections={recommendedStepSections}
            totalReplacementCost={totalReplacementCost}
            actionBtnCls={actionBtnCls}
            onExportCSV={() => exportStepsCSV(recommendedStepSections)}
            onExportXLSX={() => exportStepsXLSX(recommendedStepSections)}
          />

          <DataCompletenessCard
            overallCompletion={overallCompletion}
            hasCompletenessData={hasCompletenessData}
            surveyCompletenessRows={surveyCompletenessRows}
            visibleAssetCompletenessRows={visibleAssetCompletenessRows}
            completenessView={completenessView}
            setCompletenessView={setCompletenessView}
            activeBelowTargetCount={activeBelowTargetCount}
            activeBelowTargetLabel={activeBelowTargetLabel}
            surveyAttributesLoading={surveyAttributesLoading}
            assetCompletenessLoading={assetCompletenessLoading}
            completionDetails={completionDetails}
            onOpenAsset={openAssetDetail}
            onOpenSurveyQuestion={openSurveyQuestion}
          />
        </div>

        <AssetDetailPanelLoader
          assetId={drawerAssetId}
          onClose={() => setDrawerAssetId(null)}
        />
      </div>
    </TooltipProvider>
  );
}
